import {useEffect, useRef, useState, useTransition} from "react";
import {useInView} from "framer-motion";
import classNames from 'classnames/bind';
import styles from './ViewpointTrigger.module.scss';
import PropTypes from "prop-types";

const cx = classNames.bind(styles);

export default function ViewpointTrigger({children, className, callback, viewPercentage = 50}) {
  const ref = useRef(null);
  const [, startTransition] = useTransition();
  const [enableAnchor, setEnableAnchor] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  
  const isAnchorInView = useInView(
    isMounted ? ref : { current: null },
    {
      margin: `9999px 0px 0px 0px`,
      once: true,
    }
  );

  // Initialize mounted state
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMounted(true);
    }
  }, []);

  // Delay anchor creation to prevent callback from firing immediately
  useEffect(() => {
    // Only run on client side to prevent hydration mismatch
    if (typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        startTransition(() => {
          setEnableAnchor(true);
        });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (isAnchorInView && enableAnchor && isMounted) {
      callback();
    }
  }, [isAnchorInView, enableAnchor, isMounted]);

  return (
    <div className={cx(['component', className])}>
      {children}

      {/* Anchor positioned at viewPercentage position */}
      <aside
        ref={ref} className={cx('anchor')}
        style={{
          top: `${viewPercentage}%`,
          display: (enableAnchor ? 'block' : 'none')
        }}
        suppressHydrationWarning
      />
    </div>
  );
}

ViewpointTrigger.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  viewPercentage: PropTypes.number,
  callback: PropTypes.func.isRequired,
};
